import { Coordinate } from 'ol/coordinate';
import EventBus from './EventBus';
import { Extent } from 'ol/extent';
import type { CoreGridSegment } from './services/zones/AutoCorePlacer';

export const MAP_EVENTS = {
  SAMPLE_ARRIVED: 'sample arrived',
  SAMPLE_LEFT: 'sample left',
  CLOSE_SAMPLE_ARRIVED: 'close sample arrived',
  CLOSE_SAMPLE_LEFT: 'close sample left',
  SAMPLE_SELECTED: 'sample selected',
  SAMPLES_UPDATED: 'map: samples updated',
  CENTER_UPDATED: 'center updated',
  CORES_NEED_UPDATE: 'cores need update',
  CLOSE_SAMPLE_NEEDS_UPDATE: 'close sample needs update',
  FIT_UPDATED: 'fit updated',
  EXTENT_UPDATED: 'extent updated',
  ROTATION_UPDATED: 'rotation updated',
  RESOLUTION_UPDATED: 'resolution updated',
  POINT_RESOLUTION_UPDATED: 'point resolution updated',
  PPM_UPDATED: 'pixels per meter updated',
  ZOOM_UPDATED: 'zoom updated',
  ZOOM_TO_MISSION: 'zoom to mission',
  SET_ZOOM: 'set zoom',
  SET_CENTER: 'set center',
  SET_ROTATION: 'set rotation',
  SET_FIT: 'set fit',
  // Fires whenever the SelectionManager's selectedFeatures collection
  // changes (add / remove / clear). Focused layers listen for this to
  // refresh their selection-visual state without owning a Collection.
  SELECTION_CHANGED: 'selection changed',
  // Fires when ONLY zones changed (geometry, labels, visibility flags).
  // Narrower than MISSION_EVENTS.UPDATED — does not trigger a core
  // rebuild. Only ZonesLayer and MapInteractionsLayer listen. Step 12
  // of docs/superpowers/plans/2026-04-08-kml-layer-split.md.
  ZONES_UPDATED: 'zones updated',
  // Fires when ONLY static points changed (pullin, parking, starting,
  // end). Does not trigger a core rebuild. Only StaticPointsLayer and
  // MapInteractionsLayer listen.
  STATIC_POINTS_UPDATED: 'static points updated',
  // Fires from SamplingTask.dumpComplete when a new SoilDumpEvent is
  // created. Carries the sample_id. Only DumpEventsLayer listens.
  DUMP_SUCCESS: 'dump success',
  // Fires when waypoints moved without a full core rebuild — e.g.
  // centroid.displace in PointsUpdater or LassoService move.
  // Only PathLayer listens.
  PATH_UPDATED: 'path updated',
  // Fires from the Technician core-placement tuning panel's "Display grid"
  // toggle. Carries the per-segment equal-area grid to draw, or null to clear.
  // Only CorePlacementGridLayer listens.
  CORE_GRID_UPDATED: 'core grid updated',
};

// Change notifiers
export function dispatchZoomUpdated(zoom: number) {
  EventBus.dispatch(MAP_EVENTS.ZOOM_UPDATED, zoom);
}

export function dispatchCloseSampleNeedsUpdate() {
  EventBus.dispatch(MAP_EVENTS.CLOSE_SAMPLE_NEEDS_UPDATE);
}

export function dispatchSamplesUpdated(sampleIds: string[]) {
  EventBus.dispatch(MAP_EVENTS.SAMPLES_UPDATED, JSON.stringify({ sampleIds }));
}

export function dispatchCoresNeedUpdate() {
  EventBus.dispatch(MAP_EVENTS.CORES_NEED_UPDATE);
}

/** Push (or clear, with `null`) the Technician core-placement grid overlay. */
export function dispatchCoreGridUpdated(grids: CoreGridSegment[] | null) {
  EventBus.dispatch(MAP_EVENTS.CORE_GRID_UPDATED, grids);
}

/**
 * Fires `ZONES_UPDATED` ONLY. Use this when a change affects zone
 * geometry/visibility/labels but NOT cores — e.g. zone rename, zone
 * type change, zone label toggle. Does NOT fire
 * `MISSION_EVENTS.UPDATED` nor `CORES_NEED_UPDATE`, so sibling layers
 * that render other feature types are unaffected.
 *
 * For zone edits that might also remove cores (e.g. cutting a field
 * polygon), use `dispatchMissionUpdated()` instead — it fires both.
 */
export function dispatchZonesUpdated() {
  EventBus.dispatch(MAP_EVENTS.ZONES_UPDATED);
}

/**
 * Fires `STATIC_POINTS_UPDATED` ONLY. Use this when only the mission
 * pullin / parking_point / starting_point / end_point changed. Does
 * NOT fire `MISSION_EVENTS.UPDATED` nor `CORES_NEED_UPDATE`.
 */
export function dispatchStaticPointsUpdated() {
  EventBus.dispatch(MAP_EVENTS.STATIC_POINTS_UPDATED);
}

export function dispatchPathUpdated() {
  EventBus.dispatch(MAP_EVENTS.PATH_UPDATED);
}

export function dispatchMapExtentUpdated(extent: Extent) {
  EventBus.dispatch(MAP_EVENTS.EXTENT_UPDATED, extent);
}

export function dispatchCenterUpdated(center: [number, number]) {
  EventBus.dispatch(MAP_EVENTS.CENTER_UPDATED, center);
}

export function dispatchRotationUpdated(rotation: number) {
  EventBus.dispatch(MAP_EVENTS.ROTATION_UPDATED, rotation);
}

export function dispatchMapResolutionUpdated(resolution: number) {
  EventBus.dispatch(MAP_EVENTS.RESOLUTION_UPDATED, resolution);
}

export function dispatchMapPointResolutionUpdated(pointResolution: number) {
  EventBus.dispatch(MAP_EVENTS.POINT_RESOLUTION_UPDATED, pointResolution);
}

export function dispatchPixelsPerMeterUpdated(pixelsPerMeter: number) {
  EventBus.dispatch(MAP_EVENTS.PPM_UPDATED, pixelsPerMeter);
}

export function dispatchFitUpdated(fit: [[number, number], [number, number]]) {
  EventBus.dispatch(MAP_EVENTS.FIT_UPDATED, fit);
}

// Setter functions
export function dispatchZoomToMission() {
  EventBus.dispatch(MAP_EVENTS.ZOOM_TO_MISSION);
}

export function dispatchSetZoom(zoom: number) {
  EventBus.dispatch(MAP_EVENTS.SET_ZOOM, zoom);
}

export function dispatchSetCenter(center: Coordinate) {
  EventBus.dispatch(MAP_EVENTS.SET_CENTER, center);
}

export function dispatchSetRotation(rotation: number) {
  EventBus.dispatch(MAP_EVENTS.SET_ROTATION, rotation);
}

export function dispatchSetExtent(extent: Extent) {
  EventBus.dispatch(MAP_EVENTS.SET_FIT, extent);
}

export function dispatchSetFit(fit: [Coordinate, Coordinate]) {
  EventBus.dispatch(MAP_EVENTS.SET_FIT, fit);
}
